import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

const apps = [
  {
    to: "/health",
    label: "Health",
    description: "Vitals, labs, and conditions",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-7 w-7">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
      </svg>
    ),
    color: "text-rose-500",
    bg: "bg-rose-50",
    live: true,
  },
  {
    to: "#",
    label: "Finance",
    description: "Spending, budgets, and goals",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-7 w-7">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75" />
      </svg>
    ),
    color: "text-emerald-500",
    bg: "bg-emerald-50",
    live: false,
  },
  {
    to: "/wearable",
    label: "Wearable",
    description: "Recovery, sleep, HRV, and workouts",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-7 w-7">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8.25v4.5m0 0h2.25M12 12.75H9.75" />
      </svg>
    ),
    color: "text-orange-500",
    bg: "bg-orange-50",
    live: true,
  },
  {
    to: "#",
    label: "Notes",
    description: "Ideas, journaling, and tasks",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-7 w-7">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
      </svg>
    ),
    color: "text-violet-500",
    bg: "bg-violet-50",
    live: false,
  },
];

export default function Dashboard() {
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate("/login");
      } else {
        setUser(session.user);
        setLoading(false);
      }
    });
  }, [navigate]);

  async function signOut() {
    await supabase.auth.signOut();
    navigate("/login");
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-sm text-gray-400 animate-pulse">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-gray-400 font-medium">Dashboard</p>
          <h1 className="text-lg font-bold text-black leading-tight">Dylan Zajac</h1>
        </div>
        <button
          onClick={signOut}
          className="text-xs text-gray-400 hover:text-black transition-colors px-3 py-1.5 border border-gray-200 rounded-full"
        >
          Sign out
        </button>
      </header>

      {/* Apps grid */}
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-12">
        <p className="text-xs tracking-[0.18em] uppercase text-gray-400 font-semibold mb-6">Apps</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {apps.map(({ to, label, description, icon, color, bg, live }) => {
            const inner = (
              <div className={`group bg-white border border-gray-100 rounded-2xl p-6 flex items-start gap-4 transition-all duration-200 ${live ? "hover:border-gray-200 hover:shadow-sm cursor-pointer" : "opacity-50 cursor-not-allowed"}`}>
                <div className={`${bg} ${color} p-3 rounded-xl flex-shrink-0`}>{icon}</div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-semibold text-black">{label}</p>
                    {!live && (
                      <span className="text-[9px] tracking-widest uppercase text-gray-300 font-medium">Soon</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed">{description}</p>
                </div>
              </div>
            );
            return live ? (
              <Link key={label} to={to}>{inner}</Link>
            ) : (
              <div key={label}>{inner}</div>
            );
          })}
        </div>
      </main>

      <p className="text-[10px] text-gray-300 text-center pb-6">
        © {year} Dylan Zajac. All rights reserved.
      </p>
    </div>
  );
}
